/**
 * Global registry of components that currently have unsaved changes.
 *
 * Fed by `useUnsavedChangesWarning` so cross-cutting concerns can act on the fact
 * that the user has unsaved form data. It serves two kinds of consumer:
 *
 *  - a passive one that only needs to know (the silent version auto-refresh in
 *    VersionChecker, which must not reload the page under a dirty form) —
 *    `hasUnsavedChanges()`;
 *  - an active one that is about to navigate PROGRAMMATICALLY and wants the dirty
 *    form to prompt first — `requestNavigationConfirmation()`. A chrome control that
 *    is a `<button>` calling `navigate()` (the module picker, a redirect after a
 *    delete) offers no anchor for the hook's capture-phase click interceptor to
 *    catch, so the click is otherwise invisible to it.
 *
 * This is intentionally module-level state (not React state): the consumers are
 * non-rendering side effects that need a synchronous, render-independent answer.
 *
 * Coverage note: only forms that route their dirty state through
 * `useUnsavedChangesWarning` are tracked here. A form with its own ad-hoc dirty
 * tracking is invisible to `hasUnsavedChanges()` and relies solely on the
 * browser's native `beforeunload` prompt as a safety net.
 */

/**
 * Asks the registering form to block a pending navigation and prompt the user.
 * The form owns the dialog and resumes (or abandons) the navigation itself.
 */
type BlockedNavigationHandler = (destination: string) => void;

const dirtyTokens = new Map<symbol, BlockedNavigationHandler | undefined>();

/**
 * Mark a token as having unsaved changes.
 *
 * `onBlockedNavigation` is what makes the token a *blocker* rather than only a
 * flag: without one, a programmatic navigator can see the dirty state but has
 * nobody to hand the prompt to, and `requestNavigationConfirmation()` lets the
 * navigation through.
 */
export function registerUnsavedChanges(
  token: symbol,
  onBlockedNavigation?: BlockedNavigationHandler,
): void {
  dirtyTokens.set(token, onBlockedNavigation);
}

/** Clear the unsaved-changes mark for a token. */
export function unregisterUnsavedChanges(token: symbol): void {
  dirtyTokens.delete(token);
}

/** True if any registered component currently has unsaved changes. */
export function hasUnsavedChanges(): boolean {
  return dirtyTokens.size > 0;
}

/**
 * Offer a pending programmatic navigation to whichever dirty form can prompt for
 * it. Returns `true` when a form took ownership — the caller must NOT navigate;
 * the form resumes the navigation itself once the user confirms. Returns `false`
 * when nothing is dirty (or nothing can prompt), and the caller navigates as usual.
 *
 * Where two dirty forms are mounted at once the MOST RECENTLY registered one
 * prompts, so exactly one dialog opens. That is the newest thing the user touched,
 * and it is the deliberate difference from the document-global anchor interceptor,
 * where each mounted form pops its own dialog.
 */
export function requestNavigationConfirmation(destination: string): boolean {
  const handlers = [...dirtyTokens.values()].filter(
    (handler): handler is BlockedNavigationHandler => handler !== undefined,
  );
  const handler = handlers[handlers.length - 1];
  if (!handler) return false;
  handler(destination);
  return true;
}
